<template>
  <ul
    v-if="navigationData"
    ref="marker-container"
    class="navigation-bar-fi"
  >
    <li
      id="navigation-bar-fi--marker"
      ref="marker"
    />

    <template v-for="(item, index) in navigationData">
      <li
        v-if="!item.mobileOnly"
        :key="item.id"
        :class="{
          'navigation-bar-fi__item--last-text': index == Object.keys(navigationData).length - 1,
          'navigation-bar-fi__item': true,
          'navigation-bar-fi__item--active': isMounted && item.isActiveSelfOrDescendant,
          'marker-mixin-marked': index === navIndex,
        }"
        data-test="navigation"
        :data-test-2="item.label"
        :data-test-3="isProducts(item) ? 'products' : null"
      >
        <template
          v-if="!item.disableSubnavigation && (item.subElements?.length || isProducts(item))"
        >
          <span
            ref="navigationItem"
            class="navigation-bar-fi__item--text"
            @click="toggleFlyout(item, index)"
          >
            {{ item.label }}
          </span>
        </template>
        <template v-else>
          <a
            :href="item.url"
            :target="item.target"
            :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined"
            class="navigation-bar-fi__item--text"
          >
            {{ item.label }}
          </a>
        </template>
      </li>
    </template>
    <template v-for="(navigation, navItemIdx) in serviceNavigation">
      <li
        v-for="(navigationElement, elemIdx) in navigation.elements"
        :key="`${navigation.title}${navigationElement.identifier}`"
        :class="{
          'navigation-bar-fi__item': true,
          'navigation-bar-fi__item--icon': true,
          'navigation-bar-fi__item--active':
            isCountrySelectPage && navigationElement?.identifier === 'selectCountry',
          'navigation-bar-fi__item--focus': !isCountrySelectActive
            ? elemIdx === navIndex
            : navIndex === navigationData.length + 10 * navItemIdx + elemIdx,
          [`navigation-bar-fi__item-${navigation.title}-${navigationElement.identifier}`]: true,
        }"
      >
        <service-navigation-item-fi
          ref="serviceNavigationItem"
          :navigation-element="navigationElement"
          @toggle-flyout-icon="toggleFlyoutIcon(10 * navItemIdx + elemIdx)"
          @login="$emit('login')"
        />
      </li>
    </template>
  </ul>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import ServiceNavigationItemFi from '@/components/header-fi/header-top-fi/service-navigation-item-fi/ServiceNavigationItemFi.vue';
import { useFischer } from '@/composables/fischerPlugin';
import { useMarker } from '@/composables/marker';
import type { ServiceNavigation, SitecoreNavigation } from '@/sitecoreState';
import { useNavigationStore } from '@/store/pinia/navigation';

defineOptions({
  name: 'NavigationBarFi',
});

const props = withDefaults(
  defineProps<{
    navigationData?: SitecoreNavigation[];
    serviceNavigation: ServiceNavigation[];
  }>(),
  {
    navigationData: () => [],
  },
);

defineEmits<{
  login: [value: void];
}>();

const navigationStore = useNavigationStore();
const { animateMarker } = useMarker();
const fischer = useFischer();

const isMounted = ref<boolean>(false);

const isCountrySelectActive = computed(() => navigationStore.isCountrySelect);
const isCountrySelectPage = computed(() => navigationStore.isCountrySelectPage);

const showFlyout = computed({
  get: () => navigationStore.showFlyout,
  set: (value) => navigationStore.setShowFlyout(value),
});

const navIndex = computed({
  get: () => navigationStore.navIndex,
  set: (value) => navigationStore.setNavIndex(value),
});

const subElements = computed({
  get: () => navigationStore.subElements,
  set: (value) => navigationStore.setSubElements(value),
});

const groupData = computed({
  get: () => navigationStore.groupData,
  set: (value) => navigationStore.setGroupData(value),
});

const showBrandArea = computed({
  get: () => navigationStore.showBrandArea,
  set: (value) => navigationStore.setShowBrandArea(value),
});

const changeMarker = async () => {
  await nextTick();
  animateMarker();
};

watch(showFlyout, (newState: boolean, oldState: boolean) => {
  if (!newState) {
    navIndex.value = -1;
    groupData.value = undefined;
    subElements.value = [];
    showBrandArea.value = true;
  }
  if (newState !== oldState) {
    changeMarker();
  }
});

watch(() => props.navigationData, changeMarker);

watch(isCountrySelectPage, (value) => {
  if (value) {
    animateMarker();
  }
});

watch(navIndex, animateMarker);

onMounted(() => {
  isMounted.value = true;
  if (navIndex.value) {
    changeMarker();
  }
});

const toggleFlyout = (item: SitecoreNavigation, index: number) => {
  if (index === navIndex.value) {
    showFlyout.value = false;
  } else {
    showFlyout.value = true;
    navIndex.value = index;
    groupData.value = item;
    subElements.value = item.subElements;
    showBrandArea.value = false;
  }
  navigationStore.setShowSearchSuggest(false);
  navigationStore.setIsCountrySelect(false);
  navigationStore.setIsProducts(item.url === fischer.pages.products().url);
  navigationStore.setIsCompany(item.url === fischer.pages.companies().url);
};

const toggleFlyoutIcon = (index: number) => {
  navIndex.value = props.navigationData.length + index;

  return changeMarker();
};

const isProducts = (item: SitecoreNavigation) => item.url === fischer.pages.products().url;
</script>

<style scoped src="./navigation-bar-fi" />
